import { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Image as ImageIcon, Receipt, ShieldCheck, FolderOpen, LinkSimple, Download, MagnifyingGlass, Envelope } from "@phosphor-icons/react";

/**
 * WoFilesTab — AppFolio-style unified attachments tab for a Work Order.
 * Pulls every file (photos, receipts, ClickUp attachments, habitability PDFs)
 * from `GET /api/jobs/:id/files` and renders them in a filterable grid.
 */
const KIND_META = {
  photo_before: { label: "Before Photos", icon: ImageIcon, color: "amber-500", short: "before" },
  photo_after:  { label: "After Photos",  icon: ImageIcon, color: "emerald-500", short: "after" },
  photo:        { label: "Photos",         icon: ImageIcon, color: "cyan-500",  short: "photo" },
  receipt:      { label: "Receipts",       icon: Receipt,   color: "orange-500", short: "receipt" },
  clickup_attachment: { label: "Invoices / PDFs", icon: FileText, color: "purple-500", short: "clickup" },
  email_mirror: { label: "Emails",         icon: Envelope,  color: "sky-500",    short: "email" },
  habitability_pdf:   { label: "Habitability",     icon: ShieldCheck, color: "indigo-500", short: "habit" },
};

export default function WoFilesTab({ jobId }) {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const resolveUrl = (u) => {
    if (!u) return "";
    if (u.startsWith("http")) return u;
    const base = API.replace(/\/api$/, "");
    const abs = `${base}${u.startsWith("/") ? "" : "/"}${u}`;
    // /api/files/* needs the auth token — <img> tags can't send headers so
    // we sneak it in on the query string (same trick fileUrl() uses).
    if (u.includes("/api/files/") && !u.includes("auth=")) {
      const token = localStorage.getItem("access_token") || "";
      return `${abs}${abs.includes("?") ? "&" : "?"}auth=${encodeURIComponent(token)}`;
    }
    return abs;
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/jobs/${jobId}/files`);
      setRows(r.data.rows || []);
      setCounts(r.data.counts || {});
    } catch (e) {
      toast.error("Failed to load files");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [jobId]);

  const filtered = useMemo(() => {
    let list = rows;
    if (filter !== "all") list = list.filter(r => r.kind === filter);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter(r => (r.name || "").toLowerCase().includes(needle) ||
                              (r.uploaded_by || "").toLowerCase().includes(needle));
    }
    return list;
  }, [rows, filter, q]);

  const total = counts.total || 0;

  return (
    <details className="mt-6 border-2 border-slate-300 bg-white shadow-[3px_3px_0_#0f172a] rounded-sm" open
      data-testid="wo-files-tab">
      <summary className="cursor-pointer px-3 py-2 flex items-center gap-2 hover:bg-slate-50 bg-slate-100">
        <FolderOpen weight="fill" size={16} className="text-slate-800"/>
        <span className="font-display font-black uppercase tracking-wider text-sm text-slate-800">
          Files
        </span>
        <span className="px-1.5 py-0 bg-slate-800 text-white font-black text-[10px] tracking-widest rounded-sm" data-testid="wo-files-total">
          {total}
        </span>
        <span className="text-[10px] text-slate-500 font-medium normal-case tracking-normal">
          · every attachment for this WO — photos · receipts · invoices · habitability
        </span>
      </summary>

      <div className="p-3 space-y-3">
        {/* Filter chips */}
        <div className="flex flex-wrap gap-1.5">
          <FilterChip label="All" count={total} active={filter === "all"} onClick={() => setFilter("all")} testid="files-filter-all"/>
          {Object.entries(KIND_META).map(([k, m]) => {
            const c = counts[k] || 0;
            if (c === 0) return null;
            return <FilterChip key={k} label={m.label} count={c} active={filter === k} onClick={() => setFilter(k)}
              color={m.color} testid={`files-filter-${m.short}`}/>;
          })}
        </div>

        {/* Search */}
        <div className="relative">
          <MagnifyingGlass weight="bold" size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by filename or uploader…"
            data-testid="wo-files-search"
            className="w-full h-8 pl-7 pr-2 border-2 border-slate-200 rounded text-xs font-medium focus:border-slate-500 focus:outline-none"/>
        </div>

        {loading ? (
          <div className="p-6 text-center text-slate-500 text-sm">Loading files…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center text-slate-500 text-sm border-2 border-dashed border-slate-200 rounded">
            {total === 0 ? "No files attached yet." : "Nothing matches that filter."}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {filtered.map(r => <FileTile key={r.id} row={r} resolveUrl={resolveUrl}/>)}
          </div>
        )}
      </div>
    </details>
  );
}

function FilterChip({ label, count, active, onClick, color = "slate-500", testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`h-7 px-2 rounded font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 border-2 ${active
        ? `bg-slate-900 text-white border-slate-900`
        : `bg-white border-slate-300 text-slate-600 hover:border-slate-500`}`}>
      {label}
      <span className={`px-1 rounded-sm text-[9px] ${active ? "bg-white/20" : `bg-${color}/10 text-${color === "slate-500" ? "slate-700" : color}`}`}>
        {count}
      </span>
    </button>
  );
}

function FileTile({ row, resolveUrl }) {
  const meta = KIND_META[row.kind] || { icon: FileText, color: "slate-400", label: row.kind };
  const Icon = meta.icon;
  const dateStr = (row.uploaded_at || "").slice(0, 10);
  return (
    <a href={resolveUrl(row.url || row.path)} target="_blank" rel="noreferrer"
      data-testid={`file-${row.id}`}
      className="group block border-2 border-slate-200 bg-white rounded overflow-hidden hover:border-slate-500 hover:shadow-[3px_3px_0_#0f172a] transition-all">
      <div className="aspect-square bg-slate-100 relative">
        {row.is_image && row.thumb_url ? (
          <img src={resolveUrl(row.thumb_url)} alt={row.name} className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = "none"; }}/>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center p-2">
            <Icon weight="fill" size={40} className={`text-${meta.color}`}/>
            <div className="mt-1 text-[9px] font-black uppercase tracking-widest text-slate-500">
              .{row.ext || "file"}
            </div>
          </div>
        )}
        <div className="absolute top-1 left-1 px-1 py-0.5 bg-black/70 text-white text-[8px] font-black uppercase tracking-widest rounded-sm">
          {meta.label}
        </div>
        <div className="absolute bottom-1 right-1 p-1 bg-white/90 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">
          {row.url && row.url.startsWith("http") ? (
            <LinkSimple weight="bold" size={12} className="text-slate-700"/>
          ) : (
            <Download weight="bold" size={12} className="text-slate-700"/>
          )}
        </div>
      </div>
      <div className="p-1.5 space-y-0.5">
        <div className="text-[10px] font-black text-slate-800 truncate">{row.name}</div>
        <div className="text-[9px] text-slate-500 flex items-center gap-1 flex-wrap">
          {dateStr && <span>{dateStr}</span>}
          {row.uploaded_by && (<><span className="opacity-50">·</span><span className="truncate">{row.uploaded_by}</span></>)}
        </div>
      </div>
    </a>
  );
}
